import type { CareerCenter } from "@/modules/careerCenter/types"
import type { I18nLocale } from "@/modules/i18n/types"
import type { ErrorTranslations } from "@/modules/jobAds/client/components/JobAdCard/SaveJobAdButton"
import type { TrackEventModules, TrackingContext } from "@/modules/tracking/types"
import type { CardProps } from "@jobteaser/spark/components/Card"
import type { StaticImageData } from "next/image"
import type { FunctionComponent, MouseEventHandler } from "react"

import { clsx } from "clsx"
import Image from "next/image"

import BuildingPicto from "@jobteaser/spark/assets/pictos/z-0/building.png"
import { Badge } from "@jobteaser/spark/components/Badge"
import { Card, CardBody, CardBodyContent, CardBodyHeader, CardFooter } from "@jobteaser/spark/components/Card"
import { Heading } from "@jobteaser/spark/components/Heading/Heading"
import { BoltFilled } from "@jobteaser/spark/components/Icons/BoltFilled"
import { Briefcase } from "@jobteaser/spark/components/Icons/Briefcase"
import { ExternalLink } from "@jobteaser/spark/components/Icons/ExternalLink"
import { FlameFilled as IconFlameFilled } from "@jobteaser/spark/components/Icons/FlameFilled"
import { LocationOutlined } from "@jobteaser/spark/components/Icons/LocationOutlined"
import { Send } from "@jobteaser/spark/components/Icons/Send"
import { Signature } from "@jobteaser/spark/components/Signature"
import { Text } from "@jobteaser/spark/components/Text/Text"

import { SchoolBadge } from "@/modules/careerCenter/components/SchoolBadge/SchoolBadge"
import { SaveJobAdButton } from "@/modules/jobAds/client/components/JobAdCard/SaveJobAdButton"
import { TrackLink } from "@/modules/tracking/components/TrackLink"

import { JobAdCardTime } from "./JobAdCardTime"

import styles from "./JobAdCard.module.css"

type JobAdCardProps = {
  alumniBadgeLabel: string
  applicationDate?: string
  companyLogoUrl: string | StaticImageData
  companyName: string
  contractDescription: string | undefined
  dataTestid: string
  displaySaveButton?: boolean
  easyApplyLabel: string
  errorSaveJobAdButtonTranslation?: ErrorTranslations
  externalJobAdLabel?: string
  id: string
  internalJobAd: boolean
  isAlumniJobad: boolean
  isSeen?: boolean
  isJobAdSaved?: boolean
  isExternalJobAd?: boolean
  isPartnerJobAd: boolean
  isApplyJobAd?: boolean
  isNativeLink?: boolean
  isSentJobAd?: boolean
  isSponsoredJobAd?: boolean
  isTargetedJobAd?: boolean
  locale: I18nLocale
  locations: string | null
  onClick?: MouseEventHandler<HTMLDivElement> | undefined
  orientation?: Exclude<CardProps["orientation"], "horizontal">
  partnerBadgeLabel: string
  postedDate: string | null
  saveJobAdButtonEditTitle?: string
  saveLabel: string
  applyJobAdLabel?: string
  sentJobAdLabel?: string
  schoolLogo: CareerCenter["logo"] | StaticImageData // will be removed soon once we stop using fake data
  schoolName: CareerCenter["shortName"]
  sponsoredJobAdLabel?: string
  targetBadgeLabel?: string
  timeZone: Intl.ResolvedDateTimeFormatOptions["timeZone"]
  title: string
  trackEventModule: TrackEventModules
  trackingContext: TrackingContext
  unSaveLabel: string
  url: string
}

export const JobAdCard: FunctionComponent<JobAdCardProps> = ({
  alumniBadgeLabel,
  applicationDate,
  companyLogoUrl,
  companyName,
  contractDescription,
  dataTestid,
  displaySaveButton = true,
  easyApplyLabel,
  errorSaveJobAdButtonTranslation,
  externalJobAdLabel,
  id,
  internalJobAd,
  isAlumniJobad,
  isExternalJobAd,
  isJobAdSaved,
  isPartnerJobAd,
  isNativeLink = false,
  isSeen,
  isSentJobAd = false,
  isApplyJobAd = false,
  isSponsoredJobAd,
  isTargetedJobAd,
  locale,
  locations,
  onClick,
  orientation = "responsive",
  partnerBadgeLabel,
  postedDate,
  saveJobAdButtonEditTitle,
  saveLabel,
  applyJobAdLabel,
  sentJobAdLabel,
  schoolLogo,
  schoolName,
  sponsoredJobAdLabel,
  targetBadgeLabel,
  timeZone,
  title,
  trackEventModule,
  trackingContext,
  unSaveLabel,
  url,
}) => {
  const isVerticalCard = orientation === "vertical"
  const hasFooterData = postedDate || isSponsoredJobAd || displaySaveButton || isExternalJobAd

  return (
    <Card
      className={clsx(styles.main, {
        [styles.seen]: isSeen,
        [styles.vertical]: isVerticalCard,
      })}
      data-testid={dataTestid}
      onClick={onClick}
      orientation={orientation}
    >
      <Signature
        avatar={
          <Image
            src={companyLogoUrl || BuildingPicto}
            alt={companyName}
            width={48}
            height={48}
            data-testid={`${dataTestid}-company-logo`}
          />
        }
        className={styles.signature}
        label={
          <Text className={styles.companyName} variant="body1" data-testid={`${dataTestid}-company-name`}>
            {companyName}
          </Text>
        }
      />
      <CardBody className={styles.body}>
        <CardBodyHeader className={styles.header}>
          {!isVerticalCard && (
            <Text className={styles.companyName} variant="body1" data-testid={`${dataTestid}-company-name`}>
              {companyName}
            </Text>
          )}
          <Heading className={styles.title} variant="body1" weight="bold" as="h3">
            <TrackLink
              className={styles.link}
              trackingProps={{
                eventProperties: {
                  context: trackingContext,
                  easy_to_apply: internalJobAd,
                  is_a_job_ad_saved: isJobAdSaved,
                  job_ad_id: id,
                  module: trackEventModule,
                  service: "jobteaser",
                },
              }}
              href={url}
              // Due to incompatibility between Page & App Router, it's better to use a native anchor tag in such cases.
              isNativeLink={isNativeLink}
            >
              {title}
            </TrackLink>
          </Heading>
        </CardBodyHeader>
        <CardBodyContent>
          <div className={styles.companyInfos}>
            {contractDescription && (
              <div className={styles.contractInfo} data-testid={`${dataTestid}-contract`}>
                <Briefcase />
                <Text variant="body1" weight="regular" as="span">
                  {contractDescription}
                </Text>
              </div>
            )}
            {locations && (
              <div className={styles.contractInfo} data-testid={`${dataTestid}-location`}>
                <LocationOutlined />
                <Text variant="body1" weight="regular" as="span">
                  {locations}
                </Text>
              </div>
            )}
          </div>

          {/* CC SCHOOL BADGES */}
          {/* TODO - ATTRACT-1428 Should be remplaced by UniLabelComponent Component */}
          {isPartnerJobAd && (
            <SchoolBadge
              data-testid={`${dataTestid}-partner`}
              label={partnerBadgeLabel}
              schoolLogo={schoolLogo}
              schoolName={schoolName}
            />
          )}
          {isTargetedJobAd && targetBadgeLabel && (
            <SchoolBadge
              data-testid={`${dataTestid}-exclusive`}
              label={targetBadgeLabel}
              schoolLogo={schoolLogo}
              schoolName={schoolName}
            />
          )}
          {isAlumniJobad && (
            <SchoolBadge
              data-testid={`${dataTestid}-alumni`}
              label={alumniBadgeLabel}
              schoolLogo={schoolLogo}
              schoolName={schoolName}
            />
          )}

          {/* INTERNAL JOBAD TAG */}
          {internalJobAd && (
            <Badge
              className={styles.easyApplyBadge}
              data-testid={`${dataTestid}-internal`}
              icon={BoltFilled}
              isFaded
              onBackground="light"
              variant="secondary"
            >
              {easyApplyLabel}
            </Badge>
          )}

          {(isSentJobAd || isApplyJobAd) && (
            <div className={styles.badges}>
              {isApplyJobAd && applyJobAdLabel && (
                <Badge className={styles.applyBadge} data-testid={`${dataTestid}-apply`} variant="primary" isFaded>
                  {applyJobAdLabel}
                </Badge>
              )}

              {isSentJobAd && sentJobAdLabel && (
                <Badge className={styles.sendBadge} data-testid={`${dataTestid}-send`} variant="primary" isFaded>
                  {sentJobAdLabel}
                </Badge>
              )}
            </div>
          )}

          {applicationDate && (
            <Text
              className={styles.applicationDate}
              variant="body1"
              weight="semiBold"
              data-testid={`${dataTestid}-application-date`}
            >
              <Send size={20} />
              {applicationDate}
            </Text>
          )}
        </CardBodyContent>
      </CardBody>
      {hasFooterData && (
        <CardFooter className={styles.footer}>
          {postedDate && !isSponsoredJobAd && <JobAdCardTime date={postedDate} locale={locale} timeZone={timeZone} />}

          {isSponsoredJobAd && sponsoredJobAdLabel && (
            <Badge
              className={styles.sponsoredBadge}
              data-testid={`${dataTestid}-sponsored`}
              icon={IconFlameFilled}
              variant="tertiary"
            >
              {sponsoredJobAdLabel}
            </Badge>
          )}

          {isExternalJobAd && (
            <Badge className={styles.sponsoredBadge} icon={ExternalLink} variant="secondary" isFaded>
              {externalJobAdLabel}
            </Badge>
          )}

          {displaySaveButton && (
            <SaveJobAdButton
              className={styles.saveJobAdButton}
              errorTranslation={errorSaveJobAdButtonTranslation}
              isJobAdSaved={isJobAdSaved}
              jobAdId={id}
              saveLabel={saveLabel}
              title={saveJobAdButtonEditTitle}
              trackEventModule={trackEventModule}
              trackingContext={trackingContext}
              unSaveLabel={unSaveLabel}
            />
          )}
        </CardFooter>
      )}
    </Card>
  )
}
